import { PanelError, bodyOf, httpStatusOf, isOutcomeUnknown, toPanelError } from '@/console/api/errors'
import {
  ConfirmGlobalInboundRequest,
  GlobalInboundRollbackOutcome,
  GlobalInboundRollbackReason,
  GlobalInboundRollbackRequest,
  GlobalInboundRollbackResultItem,
  MwmsCancelMode,
} from '@/console/api/types'

/**
 * 글로벌 입고확정 롤백 · 재확정 패널의 순수 로직(docs/GLOBAL_INBOUND_ROLLBACK_API.md).
 * 화면 컴포넌트는 이 모듈이 계산한 값만 그린다.
 */
export const MAX_INBOUND_DOCUMENT_NUMBERS = 60
export const MAX_INBOUND_DOCUMENT_NUMBER_LENGTH = 50
/** 백엔드 예산 — 문서당 1초(§6.6). 진행 안내의 최대 대기 시간을 계산하는 데 쓴다. */
export const SECONDS_PER_DOCUMENT = 1

export const GLOBAL_SHIPPER_CODE = 'MUSINSAGLOBAL'
/** 신여주Global 창고. */
export const GLOBAL_WAREHOUSE_CODE = 'NWH09'
export const RECONFIRM_REQUEST: ConfirmGlobalInboundRequest = { shipperCode: GLOBAL_SHIPPER_CODE, warehouseCode: GLOBAL_WAREHOUSE_CODE }

export const ROLLBACK_OUTCOME = {
  ROLLBACKABLE: 'ROLLBACKABLE',
  ROLLED_BACK: 'ROLLED_BACK',
  ALREADY_ROLLED_BACK: 'ALREADY_ROLLED_BACK',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
} as const satisfies Record<GlobalInboundRollbackOutcome, GlobalInboundRollbackOutcome>

const MWMS_CANCELED_DB_FAILED: GlobalInboundRollbackReason = 'MWMS_CANCELED_DB_FAILED'
const SKIPPED_BY_OPERATOR: MwmsCancelMode = 'SKIPPED_BY_OPERATOR'

export type RollbackTone = 'good' | 'warn' | 'crit' | 'neutral' | 'strong'

export const OUTCOME_PRESENTATION = {
  ROLLBACKABLE: { label: '롤백 가능', tone: 'strong' },
  ROLLED_BACK: { label: '롤백됨', tone: 'good' },
  ALREADY_ROLLED_BACK: { label: '이미 롤백됨 · 조치 불필요', tone: 'neutral' },
  REJECTED: { label: '롤백 불가', tone: 'neutral' },
  FAILED: { label: '실패', tone: 'crit' },
} satisfies Record<GlobalInboundRollbackOutcome, { label: string; tone: RollbackTone }>

export const OUTCOME_ORDER = Object.keys(OUTCOME_PRESENTATION) as GlobalInboundRollbackOutcome[]

export const REASON_GUIDE = {
  NOT_FOUND: { meaning: '그 문서번호로 입고지시를 찾지 못했어요', action: '문서번호를 다시 확인하세요' },
  NOT_GLOBAL_CONFIRM: { meaning: '2.0 글로벌 입고확정이 만든 문서가 아니에요', action: '이 기능의 대상이 아니에요' },
  ACTUAL_QTY_EXISTS: { meaning: '입고 실적 수량이 이미 잡혀 있어요', action: 'MWMS 팀과 협의하세요. 이 기능으로는 끝이에요' },
  RESULT_RECEIVED: {
    meaning: '실적이 적재됐지만 아직 내역에 누적되기 전이에요',
    action: '잠시 뒤 다시 판정하세요. 실적 수량 있음으로 바뀌어요',
  },
  LES_DESTINATION: { meaning: '목적지가 LES 인 컷이에요', action: '이 기능의 대상이 아니에요' },
  NOT_RELEASED_YET: { meaning: 'ASN 전송이 아직 끝나지 않았어요', action: '전송이 끝나길 기다린 뒤 다시 판정하세요' },
  POST_TASK_PENDING: { meaning: '컷 후처리가 아직 끝나지 않았어요', action: '후처리가 끝나길 기다린 뒤 다시 판정하세요' },
  LEDGER_MISMATCH: { meaning: '지시와 원장의 키 집합이 어긋나요', action: '개별 조사가 필요해요' },
  MWMS_CANCEL_FAILED: { meaning: 'MWMS 취소 호출이 실패했어요. MOMS 는 바뀌지 않았어요', action: 'MWMS 화면을 확인한 뒤 다시 판정·실행하세요' },
  MWMS_CANCELED_DB_FAILED: {
    meaning: 'MWMS 는 취소됐는데 그 뒤 DB 쓰기가 실패했어요. MOMS 는 바뀌지 않았어요',
    action: 'mWMS 화면에서 취소를 확인한 뒤 체크하고 다시 판정하세요',
  },
} satisfies Record<GlobalInboundRollbackReason, { meaning: string; action: string }>

export const MWMS_CANCEL_LABEL = {
  REQUIRED: 'mWMS 입고취소 전송',
  ALREADY_CANCELED: '이미 전부 취소 · 전송 안 함',
  SKIPPED_BY_OPERATOR: '운영자 확인으로 건너뜀',
} satisfies Record<MwmsCancelMode, string>

export type ParsedInboundDocumentNumbers = {
  inboundDocumentNumbers: string[]
  tooLong: string[]
  duplicates: number
  overLimit: boolean
}

/** 줄 단위로 나눠 trim·빈 줄 제거·중복 제거(입력 순서 유지)를 한다. 60건 상한은 중복 제거 후 기준이다(§6.3). */
export const parseInboundDocumentNumbers = (raw: string): ParsedInboundDocumentNumbers => {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
  const inboundDocumentNumbers = [...new Set(lines)]

  return {
    inboundDocumentNumbers,
    tooLong: inboundDocumentNumbers.filter((documentNumber) => documentNumber.length > MAX_INBOUND_DOCUMENT_NUMBER_LENGTH),
    duplicates: lines.length - inboundDocumentNumbers.length,
    overLimit: inboundDocumentNumbers.length > MAX_INBOUND_DOCUMENT_NUMBERS,
  }
}

export const isJudgeableInput = (parsed: ParsedInboundDocumentNumbers): boolean =>
  parsed.inboundDocumentNumbers.length > 0 && parsed.tooLong.length === 0 && !parsed.overLimit

export const estimatedMaxSeconds = (documentCount: number): number => documentCount * SECONDS_PER_DOCUMENT

/** 판정 한 번의 입력과 결과. 실행은 이 판정에 쓴 입력이 지금 입력과 같을 때만 허용한다. */
export type RollbackJudgement = {
  inboundDocumentNumbers: string[]
  mwmsAlreadyCanceledDocumentNumbers: string[]
  results: GlobalInboundRollbackResultItem[]
}

const sameDocumentNumbers = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((documentNumber, index) => documentNumber === right[index])

export const isJudgementCurrent = (judgement: RollbackJudgement, dryRunRequest: GlobalInboundRollbackRequest): boolean =>
  sameDocumentNumbers(judgement.inboundDocumentNumbers, dryRunRequest.inboundDocumentNumbers) &&
  sameDocumentNumbers(judgement.mwmsAlreadyCanceledDocumentNumbers, dryRunRequest.mwmsAlreadyCanceledDocumentNumbers)

/** 실행 결과가 `FAILED(MWMS_CANCELED_DB_FAILED)` 인 문서만 운영자가 "mWMS 취소 확인" 을 체크할 수 있다(§6.4). */
export const needsMwmsCancelConfirmation = (item: GlobalInboundRollbackResultItem): boolean =>
  item.outcome === ROLLBACK_OUTCOME.FAILED && item.reason === MWMS_CANCELED_DB_FAILED

/** 건너뛰기 목록은 요청 문서의 부분집합이어야 하므로(§3.1) 지금 입력에 있는 확인 문서만 싣는다. */
export const buildDryRunRequest = (
  inboundDocumentNumbers: string[],
  mwmsCancelConfirmedDocumentNumbers: string[],
): GlobalInboundRollbackRequest => ({
  inboundDocumentNumbers,
  mwmsAlreadyCanceledDocumentNumbers: inboundDocumentNumbers.filter((documentNumber) =>
    mwmsCancelConfirmedDocumentNumbers.includes(documentNumber),
  ),
  dryRun: true,
})

/**
 * `ROLLBACKABLE` 이어야 선택할 수 있다. mWMS 취소를 건너뛰게 한 문서는 판정이 그 요청을 받아들였다는
 * 증거(`mwmsCancel === SKIPPED_BY_OPERATOR`)까지 있어야 한다(§6.4).
 */
export const isSelectableForRollback = (item: GlobalInboundRollbackResultItem, judgement: RollbackJudgement): boolean => {
  if (item.outcome !== ROLLBACK_OUTCOME.ROLLBACKABLE) {
    return false
  }

  const operatorSkipped = judgement.mwmsAlreadyCanceledDocumentNumbers.includes(item.inboundDocumentNumber)
  return !operatorSkipped || item.mwmsCancel === SKIPPED_BY_OPERATOR
}

export const selectableDocumentNumbers = (judgement: RollbackJudgement): string[] =>
  judgement.results.filter((item) => isSelectableForRollback(item, judgement)).map((item) => item.inboundDocumentNumber)

/** 선택 중 판정이 허락한 문서만, 판정 순서대로 싣는다. 건너뛰기 목록도 판정에서 확인된 것만 넘긴다. */
export const buildExecutionRequest = (judgement: RollbackJudgement, selectedDocumentNumbers: string[]): GlobalInboundRollbackRequest => {
  const documentsForRollback = selectableDocumentNumbers(judgement).filter((documentNumber) => selectedDocumentNumbers.includes(documentNumber))

  return {
    inboundDocumentNumbers: documentsForRollback,
    mwmsAlreadyCanceledDocumentNumbers: documentsForRollback.filter((documentNumber) =>
      judgement.mwmsAlreadyCanceledDocumentNumbers.includes(documentNumber),
    ),
    dryRun: false,
  }
}

export const countByOutcome = (results: GlobalInboundRollbackResultItem[]): { outcome: GlobalInboundRollbackOutcome; count: number }[] =>
  OUTCOME_ORDER.map((outcome) => ({ outcome, count: results.filter((item) => item.outcome === outcome).length })).filter((row) => row.count > 0)

/** 대부분이 거부인 것이 정상이라(§7) 사유별 건수를 많은 순으로 묶는다. */
export const countByReason = (results: GlobalInboundRollbackResultItem[]): { reason: GlobalInboundRollbackReason; count: number }[] => {
  const counts = new Map<GlobalInboundRollbackReason, number>()
  results.forEach((item) => {
    if (item.reason) {
      counts.set(item.reason, (counts.get(item.reason) ?? 0) + 1)
    }
  })

  return [...counts.entries()].map(([reason, count]) => ({ reason, count })).sort((left, right) => right.count - left.count)
}

export const hasRolledBack = (results: GlobalInboundRollbackResultItem[]): boolean =>
  results.some((item) => item.outcome === ROLLBACK_OUTCOME.ROLLED_BACK)

export const ROLLBACK_FORBIDDEN_MESSAGE = '롤백 권한이 없어요. (MOMS_ENG · MUSINSA12 · MUSINSA0 · MASTER 중 하나 필요)'
export const RECONFIRM_FORBIDDEN_MESSAGE = '재확정 권한이 없어요. (MUSINSAGLOBAL 화주 쓰기 권한 필요)'
export const FEATURE_FLAG_OFF_FALLBACK_MESSAGE = '롤백 기능(feature flag)이 꺼져 있어요.'
export const ROLLBACK_NO_RESPONSE_MESSAGE = '응답을 받지 못했어요. 서버 처리는 계속될 수 있으니 잠시 뒤 다시 판정해 결과를 확인하세요.'
export const RECONFIRM_NO_RESPONSE_MESSAGE = '응답을 받지 못했어요. 서버에서 재확정이 끝났을 수 있으니 GL01 화면에서 확인한 뒤 다시 누르세요.'
export const SERVER_MAY_STILL_BE_PROCESSING_MESSAGE = '서버가 아직 처리 중일 수 있어요. 다시 판정해 결과를 확인한 뒤 재확정하세요.'

const HTTP_CONFLICT = 409
const HTTP_INTERNAL_SERVER_ERROR = 500

const metaMessageOf = (error: unknown): string | undefined => (bodyOf(error) as { meta?: { message?: string } } | undefined)?.meta?.message

const flagOffMessageOf = (error: unknown): string =>
  (bodyOf(error) as { message?: string } | undefined)?.message ?? FEATURE_FLAG_OFF_FALLBACK_MESSAGE

/**
 * 409 는 성공 본문 모양의 `message` 를 보여준다. 500 의 `meta.message` 처리분 요약은 중단 전에 커밋된
 * 문서를 알려 주는 유일한 경로라 가공하지 않는다(§3.4). 나머지는 콘솔 공통 형식을 따른다.
 */
export const interpretRollbackError = (error: unknown): PanelError => {
  const status = httpStatusOf(error)
  if (status === HTTP_CONFLICT) {
    return { kind: 'other', message: flagOffMessageOf(error) }
  }

  const metaMessage = metaMessageOf(error)
  if (status === HTTP_INTERNAL_SERVER_ERROR && metaMessage) {
    return { kind: 'other', message: metaMessage }
  }

  if (isOutcomeUnknown(error)) {
    return { kind: 'other', message: ROLLBACK_NO_RESPONSE_MESSAGE }
  }

  return toPanelError(error, ROLLBACK_FORBIDDEN_MESSAGE)
}

export const interpretReconfirmError = (error: unknown): PanelError =>
  isOutcomeUnknown(error) ? { kind: 'other', message: RECONFIRM_NO_RESPONSE_MESSAGE } : toPanelError(error, RECONFIRM_FORBIDDEN_MESSAGE)

/** 실행이 실패해도 500 은 중단 전에 커밋된 문서가, 응답 없음·504 는 서버가 이어서 처리한 문서가 있을 수 있다. */
export const mayHaveRolledBackDespiteFailure = (error: unknown): boolean =>
  isOutcomeUnknown(error) || httpStatusOf(error) === HTTP_INTERNAL_SERVER_ERROR
